<script>
    import BaseTab from "@/tabs/mixins/BaseTab.js";
    import { getConfig, getErrorMessage, klinikenApi } from "@/api";
    import { openDialog } from "@/utils";
    import { mapGetters } from "vuex";
    import dialogEvents from "@/dialogEvents";
    import PlusCircleWhite from "@/assets/plus_circle_white.svg";
    import PlusCircleBlue from "@/assets/plus_circle_blue.svg";
    import EditCircleBlue from "@/assets/icons/edit_circle_blue.svg";
    import PositiveNotification from "@/assets/positive_notification.svg";
    import { cloneDeep, isEqual } from "lodash";
    import JournalAnteckning from "@/tabs/Journalanteckningar/Journalanteckning.vue";
    import Button from "@/components/Button.vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import KHeader from "@/components/KHeader.vue";
    import KSection from "@/components/generic/KSection.vue";
    import KAutocomplete from "@/components/forms/inputs/Autocomplete/KAutocomplete.vue";
    import KNumber from "@/components/forms/inputs/Number/KNumber.vue";
    import KText from "@/components/forms/inputs/Text/KText.vue";
    import KRepeater from "@/components/forms/groups/Repeater/KRepeater.vue";

    const statusOptions = [
        { value: "Ej avstämd", label: "Ej avstämd" },
        { value: "Närvarande", label: "Närvarande" },
        { value: "Skada sker", label: "Skada sker" },
        { value: "Semester", label: "Semester" },
        { value: "Deltar ej", label: "Deltar ej" },
        { value: "Sjukdom", label: "Sjukdom" },
        { value: "Skadad", label: "Skadad" },
    ];

    const defaultAktivitet = {
        langd: null,
    };

    const defaultAvstamningar = { avstamningar: [] };
    export default {
        extends: BaseTab,
        name: "FormAktivitet",
        tabName: "Aktivitet",

        components: {
            Button,
            KForm,
            KHeader,
            KSection,
            KAutocomplete,
            KNumber,
            KText,
            KRepeater,
        },

        props: {
            tabId: Number,
            parameters: {
                type: Object,
                required: false,
            },
        },

        data: function () {
            return {
                PlusCircleWhite,
                PlusCircleBlue,
                PositiveNotification,
                EditCircleBlue,
                defaultAktivitet: cloneDeep(defaultAktivitet),
                formAktivitet: cloneDeep(defaultAktivitet),
                defaultAvstamningar: cloneDeep(defaultAvstamningar),
                formAvstamningar: cloneDeep(defaultAvstamningar),
                markedAvstamningar: [],
                isLoading: true,
                isInitialAktivitetUpdate: true,
                isInitialAvstamningarUpdate: true,
            };
        },

        computed: {
            ...mapGetters("userData", ["currentProfile"]),
            hasUnsavedData() {
                return this.aktivitetHasUnsavedData || this.avstamningarHasUnsavedData;
            },
            aktivitetHasUnsavedData() {
                return !isEqual(this.defaultAktivitet, this.formAktivitet);
            },
            avstamningarHasUnsavedData() {
                return !isEqual(this.defaultAvstamningar, this.formAvstamningar);
            },
            heading() {
                return this.formAktivitet.id === undefined ? "Skapa ny aktivitet" : "Ändra på aktivitet";
            },
            statusOptions() {
                return statusOptions;
            },
        },

        methods: {
            getAktivitetInitialValues() {
                return Promise.resolve(this.formAktivitet);
            },
            onAktivitetUpdated(formData) {
                this.formAktivitet = { ...this.formAktivitet, ...formData };
                if (this.isInitialAktivitetUpdate) {
                    this.defaultAktivitet = cloneDeep(this.formAktivitet);
                    this.isInitialAktivitetUpdate = false;
                }
            },
            async submitAktivitet(formData) {
                const data = { ...formData, id: this.formAktivitet.id };
                const action = data.id ? "skreg/aktiviteter/updateAktivitet" : "skreg/aktiviteter/createAktivitet";
                try {
                    const response = await this.$store.dispatch(action, { aktivitet: data });
                    this.formAktivitet = response.data;
                    this.isInitialAktivitetUpdate = true;
                    this.$refs.aktivitetForm?.setFormValues({
                        values: this.toAktivitetFormValues(response.data),
                        reset: true,
                    });
                    await this.loadAvstamningar();
                } catch (e) {
                    openDialog("Kunde inte spara aktiviteten", "error");
                }
            },
            // KDateTimepicker type="time" kräver "YYYY-MM-DD HH:mm" för att validera korrekt
            // när inputMaskValue redan är satt. Servern returnerar "HH:MM:SS".
            applyAutocompleteDisplays(aktivitet) {
                return {
                    ...aktivitet,
                    lag: aktivitet.lag_display
                        ? { value: aktivitet.lag, label: aktivitet.lag_display.namn }
                        : aktivitet.lag,
                    plats: aktivitet.plats_display
                        ? { value: aktivitet.plats, label: aktivitet.plats_display.namn }
                        : aktivitet.plats,
                    aktivitetstyp: aktivitet.aktivitetstyp_display
                        ? { value: aktivitet.aktivitetstyp, label: aktivitet.aktivitetstyp_display.namn }
                        : aktivitet.aktivitetstyp,
                    underlag: aktivitet.underlag_display
                        ? { value: aktivitet.underlag, label: aktivitet.underlag_display.namn }
                        : aktivitet.underlag,
                };
            },
            toAktivitetFormValues(aktivitet) {
                const values = this.applyAutocompleteDisplays({ ...aktivitet });
                if (values.tid && values.datum) {
                    const [h, m] = values.tid.split(":");
                    values.tid = `${values.datum} ${h}:${m ?? "00"}`;
                }
                return values;
            },
            cancelAktivitet() {
                this.closeTab(this.hasUnsavedData);
            },
            skapaYtterligareAktiviet() {
                this.formAktivitet = {};
                this.defaultAktivitet = {};
                this.isInitialAktivitetUpdate = true;
                this.$refs.aktivitetForm?.setFormValues({ values: {}, reset: true });
                this.formAvstamningar = { avstamningar: [] };
                this.defaultAvstamningar = { avstamningar: [] };
            },
            toRepeaterRows(avstamningar) {
                return avstamningar.map((av) => ({
                    status: av.status,
                    narvarande: av.narvarande,
                    kommentar: av.kommentar,
                }));
            },
            getAvstamningarInitialValues() {
                return Promise.resolve({ avstamningar: this.toRepeaterRows(this.formAvstamningar.avstamningar) });
            },
            onAvstamningarUpdated(formData) {
                const rows = formData.avstamningar ?? [];
                const updated = cloneDeep(this.formAvstamningar.avstamningar);
                rows.forEach((row, i) => {
                    if (!updated[i]) return;
                    if (row.status !== undefined) updated[i].status = row.status;
                    if (row.narvarande !== undefined) updated[i].narvarande = row.narvarande;
                    if (row.kommentar !== undefined) updated[i].kommentar = row.kommentar;
                });
                this.formAvstamningar = { ...this.formAvstamningar, avstamningar: updated };
                if (this.isInitialAvstamningarUpdate) {
                    this.defaultAvstamningar = cloneDeep(this.formAvstamningar);
                    this.isInitialAvstamningarUpdate = false;
                }
            },
            async loadAvstamningar() {
                try {
                    const data = await this.$store.dispatch("skreg/aktiviteter/fetchAvstamningar", {
                        aktivitetId: this.formAktivitet.id,
                    });
                    const avstamningar = cloneDeep(data);
                    this.formAvstamningar = { avstamningar };
                    this.defaultAvstamningar = cloneDeep(this.formAvstamningar);
                    this.isInitialAvstamningarUpdate = true;
                    this.$refs.avstamningarForm?.setFormValues({
                        values: { avstamningar: this.toRepeaterRows(avstamningar) },
                        reset: true,
                    });
                } catch (e) {
                    openDialog(
                        "Det gick inte att ladda avstämningarna för aktiviteten: " + getErrorMessage(e),
                        "warning"
                    );
                }
            },
            async copyStatusFromPrevious() {
                try {
                    const previous = await this.$store.dispatch("skreg/aktiviteter/fetchPreviousAvstamningar", {
                        aktivitetId: this.formAktivitet.id,
                    });
                    const updated = cloneDeep(this.formAvstamningar.avstamningar);
                    for (const item of previous) {
                        const index = updated.findIndex((i) => i.spelarelag === item.spelarelag);
                        if (index === -1) continue;

                        if (item.status === "Skada sker") {
                            updated[index].status = "Skadad";
                        } else {
                            updated[index].status = item.status;
                        }
                        if (item.status === "Närvarande") {
                            updated[index].narvarande = this.formAktivitet.langd;
                        }
                        if (updated[index].status === "Skadad") {
                            updated[index].journalanteckning = item.journalanteckning;
                            const di = this.defaultAvstamningar.avstamningar.findIndex(
                                (i) => i.spelarelag === item.spelarelag
                            );
                            if (di !== -1) {
                                this.defaultAvstamningar.avstamningar[di].status = updated[index].status;
                                this.defaultAvstamningar.avstamningar[di].journalanteckning = item.journalanteckning;
                            }
                        }
                        if (item.status === "Sjukdom" && item.journalanteckning) {
                            const fetchRes = await klinikenApi.get(
                                `anteckning/journalanteckningar/${item.journalanteckning}`,
                                getConfig()
                            );
                            if (fetchRes.status === 200 && !fetchRes.data.is_signed) {
                                updated[index].status = "Sjukdom";
                                updated[index].journalanteckning = item.journalanteckning;
                            }
                        }
                    }
                    this.formAvstamningar = { ...this.formAvstamningar, avstamningar: updated };
                    this.$refs.avstamningarForm?.setFormValues({
                        values: { avstamningar: this.toRepeaterRows(updated) },
                        reset: true,
                    });
                } catch (e) {
                    openDialog("Det gick inte att kopiera status från föregående aktivitet", "warning");
                }
            },
            getPatientNamn(index) {
                return this.formAvstamningar?.avstamningar?.[index]?.patient_display?.namn_reverse ?? "";
            },
            onStatusChange(value, index) {
                const updated = cloneDeep(this.formAvstamningar.avstamningar);
                if (updated[index]) {
                    updated[index].status = value;
                    updated[index].narvarande = value === "Närvarande" ? this.formAktivitet.langd : 0;
                    this.formAvstamningar = { ...this.formAvstamningar, avstamningar: updated };
                    this.$refs.avstamningarForm?.setFormValues({
                        values: { avstamningar: this.toRepeaterRows(updated) },
                    });
                }
            },
            setMarkedStatus(value) {
                if (!value) return;
                const updated = cloneDeep(this.formAvstamningar.avstamningar);
                this.markedAvstamningar.forEach((index) => {
                    if (updated[index]) {
                        updated[index].status = value;
                        updated[index].narvarande = value === "Närvarande" ? this.formAktivitet.langd : 0;
                    }
                });
                this.markedAvstamningar = [];
                this.formAvstamningar = { ...this.formAvstamningar, avstamningar: updated };
                this.$refs.avstamningarForm?.setFormValues({
                    values: { avstamningar: this.toRepeaterRows(updated), statusMarkerade: null },
                });
            },
            statusColor(index) {
                return this.formAvstamningar?.avstamningar?.[index]?.status ?? "";
            },
            async submitAvstamningar() {
                const doSave = async () => {
                    await this.putAvstamningar(this.formAvstamningar.avstamningar);
                };
                if (this.aktivitetHasUnsavedData) {
                    dialogEvents.$emit("open", {
                        type: "warning",
                        description: "Du har osparade ändringar i aktiviteten. Vill du även spara dessa?",
                        buttons: [
                            {
                                title: "Ja",
                                type: "secondary",
                                action: async () => {
                                    try {
                                        const action = this.formAktivitet.id
                                            ? "skreg/aktiviteter/updateAktivitet"
                                            : "skreg/aktiviteter/createAktivitet";
                                        const response = await this.$store.dispatch(action, {
                                            aktivitet: this.formAktivitet,
                                        });
                                        this.defaultAktivitet = cloneDeep(response.data);
                                    } catch (e) {
                                        openDialog(getErrorMessage(e));
                                    }
                                    await doSave();
                                },
                            },
                            {
                                title: "Nej",
                                type: "secondary",
                                action: async () => {
                                    await doSave();
                                    this.formAktivitet = cloneDeep(this.defaultAktivitet);
                                },
                            },
                            {
                                title: "Avbryt",
                                type: "primary",
                            },
                        ],
                    });
                } else {
                    await doSave();
                }
            },
            async putAvstamningar(avstamningar) {
                try {
                    const data = await this.$store.dispatch("skreg/aktiviteter/saveAvstamningar", {
                        aktivitetId: this.formAktivitet.id,
                        avstamningar,
                    });
                    this.formAvstamningar = { avstamningar: cloneDeep(data) };
                    this.defaultAvstamningar = cloneDeep(this.formAvstamningar);
                    this.isInitialAvstamningarUpdate = true;
                    this.$refs.avstamningarForm?.setFormValues({
                        values: { avstamningar: this.toRepeaterRows(this.formAvstamningar.avstamningar) },
                        reset: true,
                    });
                } catch (e) {
                    openDialog("Kunde inte spara avstämningar", "error");
                }
            },
            cancelAvstamningar() {
                if (this.avstamningarHasUnsavedData) {
                    dialogEvents.$emit("open", {
                        type: "warning",
                        description: "Du har inte sparat. Vill du stänga utan att spara?",
                        buttons: [
                            {
                                title: "Fortsätt redigera",
                                type: "secondary",
                            },
                            {
                                title: "Ja, stäng utan att spara",
                                type: "primary",
                                action: () => {
                                    this.$store.dispatch("tabs/closeTab", this.tabId);
                                },
                            },
                        ],
                    });
                }
            },
            showJournalanteckning(index) {
                let initialAvstamning = this.defaultAvstamningar.avstamningar[index];
                let avstamning = this.formAvstamningar.avstamningar[index];
                return (
                    initialAvstamning !== undefined &&
                    avstamning !== undefined &&
                    initialAvstamning.status === avstamning.status &&
                    (initialAvstamning.status === "Skada sker" ||
                        initialAvstamning.status === "Sjukdom" ||
                        (initialAvstamning.status === "Skadad" && this.hasJournalanteckning(index)))
                );
            },
            hasJournalanteckning(index) {
                let avstamning = this.defaultAvstamningar.avstamningar[index];
                return avstamning.journalanteckning !== null;
            },
            async openJournalanteckning(index) {
                let avstamning = this.defaultAvstamningar.avstamningar[index];
                if (avstamning.journalanteckning !== null) {
                    let id = avstamning.journalanteckning;
                    await this.$store.dispatch("tabs/closeTab", JournalAnteckning, { root: true });
                    await this.$store.dispatch("journalanteckningar/getJournalanteckning", id);
                    let anteckning = this.$store.getters["journalanteckningar/anteckning"];
                    await this.$store.dispatch("patientData/loadFromBackend", { id: anteckning.patient.id });
                    await this.$store.dispatch("tabs/openTab", { component: JournalAnteckning });
                }
            },
            async createJournalanteckning(index) {
                const avstamning = this.defaultAvstamningar.avstamningar[index];
                if (
                    avstamning.journalanteckning === null &&
                    (avstamning.status === "Skada sker" ||
                        avstamning.status === "Sjukdom" ||
                        avstamning.status === "Skadad")
                ) {
                    try {
                        const data = await this.$store.dispatch("skreg/aktiviteter/createJournalanteckning", {
                            avstamningId: avstamning.id,
                        });
                        this.defaultAvstamningar.avstamningar[index].journalanteckning = data["id"];
                        this.formAvstamningar.avstamningar[index].journalanteckning = data["id"];
                    } catch (e) {
                        openDialog("Kunde inte skapa journalanteckning", "error");
                    }
                }
            },
            getKortnamn(index) {
                let avstamning = this.defaultAvstamningar.avstamningar[index];
                if (avstamning !== undefined) {
                    if (avstamning.status === "Skada sker" || avstamning.status === "Skadad") return "skadekort";
                    if (avstamning.status === "Sjukdom") return "sjukdomskort";
                }
                return "OKÄND";
            },
            toggleMarked(index, event) {
                if (event.target.checked) {
                    this.markedAvstamningar = [...new Set([...this.markedAvstamningar, index])];
                } else {
                    this.markedAvstamningar = this.markedAvstamningar.filter((i) => i !== index);
                }
            },
            markAll(event) {
                if (event.target.checked) {
                    this.markedAvstamningar = this.formAvstamningar.avstamningar.map((_, index) => index);
                } else {
                    this.markedAvstamningar = [];
                }
            },
        },

        watch: {
            markedAvstamningar(val) {
                this.$refs.statusMarkeradeRef?.setDisabled({ value: val.length < 1 });
            },
        },

        async created() {
            if (this.parameters !== undefined && "aktivitet" in this.parameters) {
                const aktivitet = this.parameters["aktivitet"];
                if (this.parameters.duplicate) {
                    this.formAktivitet = this.applyAutocompleteDisplays({
                        lag: aktivitet.lag,
                        plats: aktivitet.plats,
                        tid: aktivitet.tid,
                        langd: aktivitet.langd,
                        aktivitetstyp: aktivitet.aktivitetstyp,
                        underlag: aktivitet.underlag,
                        beskrivning: aktivitet.beskrivning,
                        lag_display: aktivitet.lag_display,
                        plats_display: aktivitet.plats_display,
                        aktivitetstyp_display: aktivitet.aktivitetstyp_display,
                        underlag_display: aktivitet.underlag_display,
                    });
                } else {
                    this.formAktivitet = this.applyAutocompleteDisplays(cloneDeep(aktivitet));
                    this.defaultAktivitet = cloneDeep(this.formAktivitet);
                }
            }

            this.isLoading = false;

            if (this.parameters !== undefined && "aktivitet" in this.parameters && !this.parameters.duplicate) {
                await this.loadAvstamningar();
            }
        },
    };
</script>

<template>
    <div v-if="!isLoading" class="aktivitet">
        <KForm
            ref="aktivitetForm"
            id="aktivitet"
            :initialValuesGetter="getAktivitetInitialValues"
            @submit="submitAktivitet"
            @updated="onAktivitetUpdated"
        >
            <template #header>
                <KHeader :heading="heading" />
            </template>

            <KSection name="aktivitet" heading="Aktivitet" number="1">
                <div class="form-content">
                    <LagAutocomplete label="Lag" validation="required" placeholder="Välj lag" />

                    <PlatsAutocomplete label="Plats" validation="required" placeholder="Välj plats" />

                    <KDateTimepicker name="datum" label="Datum" validation="required" />

                    <KDateTimepicker name="tid" label="Tid" type="time" validation="required" :showNowButton="false" />

                    <KNumber name="langd" label="Längd i minuter" validation="required" :min="0" />

                    <AktivitetstypAutocomplete
                        class="aktivitetstyp"
                        label="Typ"
                        validation="required"
                        placeholder="Välj typ"
                    />

                    <UnderlagAutocomplete label="Underlag" validation="required" placeholder="Välj underlag" />

                    <KText name="beskrivning" label="Beskrivning" />
                </div>
            </KSection>

            <template #actions>
                <span v-if="formAktivitet.id !== undefined" class="aktiviten_skapad">
                    <img alt="" :src="PositiveNotification" />
                    Aktiviten skapad
                </span>

                <Button :disabled="!aktivitetHasUnsavedData" size="primary" type="submit">
                    <template v-if="formAktivitet.id"> Uppdatera aktivitet</template>
                    <template v-else><img alt="+" :src="PlusCircleWhite" /> Skapa aktivitet</template>
                </Button>

                <Button
                    v-if="formAktivitet.id"
                    :disabled="aktivitetHasUnsavedData || avstamningarHasUnsavedData"
                    @click.prevent="skapaYtterligareAktiviet"
                    size="secondary"
                    type="button"
                >
                    Skapa ytterligare aktivitet
                </Button>

                <Button @click="cancelAktivitet()" size="secondary" type="button">Avbryt</Button>
            </template>
        </KForm>

        <KForm
            v-if="formAktivitet.id !== undefined"
            ref="avstamningarForm"
            id="avstamningar"
            :initialValuesGetter="getAvstamningarInitialValues"
            @submit="submitAvstamningar"
            @updated="onAvstamningarUpdated"
        >
            <KSection name="narvaro" heading="Stäm av närvaro" number="2">
                <div class="avstamningar">
                    <p>Om du vill stämma av aktiviteten senare går det bra.</p>
                    <Button @click.prevent="copyStatusFromPrevious" size="secondary" role="button" type="button">
                        Kopiera status från föregående
                    </Button>
                    <div class="table">
                        <div class="tr th">
                            <div class="td">
                                <label class="checkbox">
                                    <input
                                        type="checkbox"
                                        :checked="
                                            formAvstamningar.avstamningar &&
                                            formAvstamningar.avstamningar.length === markedAvstamningar.length
                                        "
                                        @change="markAll($event)"
                                    />
                                    <span></span>
                                </label>
                            </div>
                            <div class="td">Deltagare</div>
                            <div class="td">Status</div>
                            <div class="td">Närvaro minuter (från start)</div>
                            <div class="td">Kommentar (frivillig)</div>
                        </div>

                        <KRepeater name="avstamningar" :addButton="false" :removeButton="false" :minimumOneRow="false">
                            <template #default="{ index, model }">
                                <div class="tr">
                                    <div class="td">
                                        <label class="checkbox">
                                            <input
                                                type="checkbox"
                                                :checked="markedAvstamningar.includes(index)"
                                                @change="toggleMarked(index, $event)"
                                            />
                                            <span></span>
                                        </label>
                                    </div>
                                    <div class="td">{{ getPatientNamn(index) }}</div>
                                    <div class="td status">
                                        <KAutocomplete
                                            name="status"
                                            label="Status"
                                            :options="statusOptions"
                                            :searchable="false"
                                            :clearable="false"
                                            :autoPosition="true"
                                            :value="model?.[index]?.status ?? null"
                                            @inputValue="({ value }) => onStatusChange(value, index)"
                                        />
                                        <div v-if="showJournalanteckning(index)" class="journalanteckning">
                                            <a
                                                v-if="hasJournalanteckning(index)"
                                                @click="openJournalanteckning(index)"
                                                role="button"
                                            >
                                                <img alt="" :src="EditCircleBlue" /> Visa {{ getKortnamn(index) }}
                                            </a>
                                            <a v-else @click="createJournalanteckning(index)" role="button">
                                                <img alt="+" :src="PlusCircleBlue" /> Skapa {{ getKortnamn(index) }}
                                            </a>
                                        </div>
                                    </div>
                                    <div class="td">
                                        <KNumber
                                            name="narvarande"
                                            label="Närvaro (minuter)"
                                            :min="0"
                                            :value="model?.[index]?.narvarande ?? null"
                                        />
                                    </div>
                                    <div class="td">
                                        <KText
                                            name="kommentar"
                                            label="Kommentar"
                                            :value="model?.[index]?.kommentar ?? null"
                                        />
                                    </div>
                                </div>
                            </template>
                        </KRepeater>
                    </div>

                    <div class="mark">
                        <div>Status markerade ({{ markedAvstamningar.length }})</div>
                        <div>
                            <KAutocomplete
                                ref="statusMarkeradeRef"
                                class="status-markerade-input"
                                name="statusMarkerade"
                                :options="statusOptions"
                                :disabled="true"
                                :searchable="false"
                                :clearable="false"
                                placeholder="Sätt status..."
                                @inputValue="({ value }) => setMarkedStatus(value)"
                            />
                        </div>
                    </div>
                </div>
            </KSection>

            <template #actions>
                <Button :disabled="!avstamningarHasUnsavedData" size="primary" type="submit"> Spara avstämning </Button>
                <Button
                    @click.prevent="cancelAvstamningar()"
                    size="secondary"
                    type="button"
                    :disabled="!avstamningarHasUnsavedData"
                >
                    Avbryt
                </Button>
            </template>
        </KForm>
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";
    @import "@/style/checkbox";

    .aktivitet {
        .aktiviten_skapad {
            display: inline-block;
            padding: 36px 6px;
            margin: 0 1rem 0 0;
        }

        .avstamningar {
            grid-column: 1 / -1;

            .table {
                display: flex;
                flex-flow: column nowrap;
                margin: 0.5rem;
                line-height: 1.5;
                border-bottom: 1px solid #d0d0d0;
                flex: 1 1 auto;

                .th {
                    display: none;
                    font-weight: normal;
                    color: $color-thead;
                }

                .th .td {
                    white-space: normal;
                }

                .tr {
                    width: 100%;
                    display: flex;
                    flex-flow: row nowrap;
                    border-bottom: 1px solid #d3d8da;
                    grid-column: 1 / -1;
                }

                .td {
                    display: flex;
                    flex-flow: row nowrap;
                    flex-grow: 10;
                    flex-basis: 0;
                    padding: 0.5em;
                    word-break: break-word;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    min-width: 0;
                    white-space: nowrap;
                    border-bottom: 1px solid #d0d0d0;

                    &:first-child {
                        flex-grow: 1;
                    }

                    &.status {
                        flex-direction: column;
                    }
                }
            }

            ::v-deep .k-repeater {
                margin: 0;

                .tr {
                    grid-column: 1 / -1;
                }
            }

            .mark {
                display: flex;
                flex-flow: row nowrap;
                background-color: $color-background-list;
                padding: 1em 2em;

                div {
                    line-height: 46px;
                    flex-grow: 5;
                    flex-basis: 0;
                    align-items: center;

                    &:first-child {
                        flex-grow: 1;
                    }
                }

                .status-markerade-input {
                    max-width: 20em;
                    background-color: #fff;
                }
            }
        }
    }
</style>
